// Fichier: RepresentativeUserModal.js
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import FormInput from "../../../components/controls/FormInput";
import UserRolesSection from "../../Admin/UserManagement/UserRolesSection";
import CompanyEditor from "./tabs/CompanyEditor.js";
import SubscriptionContent from "../../BecomePartner/SubscriptionForRepresentative.js";
import InternalNotes from "./tabs/InternalNotes.js";
import FollowUpsTab from "./tabs/FollowUps.js";
import { supabase } from "../../../SupabaseClient.js";

export default function RepresentativeUserModal({
  isOpen,
  onClose,
  onSave,
  user,
  availableRoles = [],
  currentUserId = null,
}) {
  const { t } = useTranslation();
  const isEditing = !!user;

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [isGoogleAccount, setIsGoogleAccount] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [activeCompanyIndex, setActiveCompanyIndex] = useState(0);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [initialRoleIds, setInitialRoleIds] = useState([]);
  const [currentUserIdReceived, setCurrentUserId] = useState(currentUserId);
  const isReadOnly = user && user.canEdit === false;

  useEffect(() => {
    if (user) {
      setEmail(user.email || "");
      setFullName(user.fullName || "");
      setIsGoogleAccount(user?.isGoogleAccount || false);
      setCompanies(user.companies || []);
      setSelectedRoleIds(user.userRoles?.map((r) => r.roleId) || []);
      setInitialRoleIds(user.userRoles?.map((r) => r.roleId) || []);
    } else {
      setEmail("");
      setFullName("");
      setIsGoogleAccount(false);
      setCompanies([]);
      setSelectedRoleIds([]);
      setInitialRoleIds([]);
    }
    setCurrentUserId(currentUserId || null);
  }, [user]);

  const handleClose = () => {
    setEmail("");
    setFullName("");
    setIsGoogleAccount(false);
    setCompanies([]);
    onClose();
  };

  const handleSave = async () => {
    const roleDiff = selectedRoleIds
      .filter((id) => !initialRoleIds.includes(id))
      .concat(initialRoleIds.filter((id) => !selectedRoleIds.includes(id)));

    if (roleDiff.length > 0) {
      const changedDescriptions = availableRoles
        .filter((r) => roleDiff.includes(r.id))
        .map((r) => r.description || r.name);

      const confirmText =
        "Les rôles suivants ont été modifiés, enregistrer ?\n\n- " +
        changedDescriptions.join("\n- ");

      const confirmed = window.confirm(confirmText);
      if (!confirmed) return;
    }

    const payload = {
      id: user?.id || null,
      email,
      fullName,
      isGoogleAccount,
      companies: companies.map((company) => ({
        ...company,
        imageUrl: company.hasChangedImageUrl
          ? (company.tempImageUrl ?? null)
          : company.imageUrl,
      })),
      userRoles: selectedRoleIds.map((roleId) => ({
        roleId,
        userId: user?.id || null,
      })),
      createdBy: currentUserIdReceived || null,
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/User`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-User-Id": currentUserIdReceived || "",
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();

        if (errorText.includes("UserAlreadyExists")) {
          alert("Un utilisateur existe déjà avec cet email.");
        } else {
          alert("Une erreur est survenue : " + errorText);
        }

        throw new Error(errorText);
      }

      // 🟡 ENVOI DU COURRIEL SEULEMENT si nouvel utilisateur
      if (!user?.id) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          //redirectTo: "https://www.ananassolutions.ca/reset-password",
          redirectTo: `${window.location.origin}/reset-password`,
        });

        if (error) {
          console.error(
            "Erreur lors de l’envoi du courriel de création de mot de passe :",
            error.message,
          );
        } else {
          console.log("Courriel envoyé pour définir le mot de passe ✔️");
        }
      }

      onClose();
      onSave(payload);
    } catch (error) {
      console.error("Erreur handleSave:", error);
    }
  };

  const renderGeneralTab = () => (
    <div className="tab-content border-base-300 bg-base-100 p-4 space-y-4">
      <FormInput
        disabled={isReadOnly}
        label={t("adminUserTable.email")}
        name="email"
        value={email}
        onChange={(name, value) => setEmail(value)}
        required
      />
      <FormInput
        disabled={isReadOnly}
        label={t("adminUserTable.fullName")}
        name="fullName"
        value={fullName}
        onChange={(name, value) => setFullName(value)}
      />
      <div className="form-control">
        <label className="cursor-pointer label">
          <span className="label-text">Compte Google ?</span>
          <input
            type="checkbox"
            className="toggle"
            checked={isGoogleAccount}
            onChange={(e) => setIsGoogleAccount(e.target.checked)}
          />
        </label>
      </div>
    </div>
  );

  return (
    <dialog className={`modal ${isOpen ? "modal-open" : ""}`}>
      <div className="modal-box w-full max-w-7xl">
        <h3 className="font-bold text-lg mb-4">
          {isEditing
            ? t("adminUserTable.editTitle")
            : t("adminUserTable.addTitle")}
        </h3>

        <div className="tabs tabs-lifted border p-2">
          <input
            type="radio"
            name="user-tabs"
            className="tab [&:checked]:bg-base-200 [&:checked]:font-bold"
            aria-label={t("adminUserTable.generalTab")}
            defaultChecked
          />
          {renderGeneralTab()}

          <input
            type="radio"
            name="user-tabs"
            className="tab [&:checked]:bg-base-200 [&:checked]:font-bold"
            aria-label={t("adminUserTable.companyTab")}
          />
          <CompanyEditor
            companies={companies}
            setCompanies={setCompanies}
            activeCompanyIndex={activeCompanyIndex}
            setActiveCompanyIndex={setActiveCompanyIndex}
            isReadOnly={isReadOnly}
            t={t}
          />

          <input
            type="radio"
            name="user-tabs"
            className="tab [&:checked]:bg-base-200 [&:checked]:font-bold"
            aria-label={t("Roles")}
          />
          <div className="tab-content border-base-300 bg-base-100 p-4 space-y-4 min-h-[400px]">
            <UserRolesSection
              values={selectedRoleIds}
              onChange={setSelectedRoleIds}
              availableRoles={availableRoles}
            />
          </div>
          {user?.id && (
            <>
              <input
                type="radio"
                name="user-tabs"
                className="tab [&:checked]:bg-base-200 [&:checked]:font-bold"
                aria-label={t("adminUserTable.subscriptionsTab")}
              />
              <div className="tab-content border-base-300 bg-base-100 p-4 space-y-4 min-h-[400px]">
                <SubscriptionContent
                  selectedPriceId={user?.subscription?.priceId || null}
                  selectedUserId={user?.id || null}
                  selectedCustomerEmail={user?.email || ""}
                />
              </div>

              <input
                type="radio"
                name="user-tabs"
                className="tab [&:checked]:bg-base-200 [&:checked]:font-bold"
                aria-label={t("subscriptionModal.internalNotes")}
              />
              <div className="tab-content border-base-300 bg-base-100 p-4 space-y-4 max-h-[450px] overflow-y-auto">
                <InternalNotes
                  internalNotes={user?.internalNotes || []}
                  setInternalNotes={(notes) => {
                    user.internalNotes = notes;
                  }}
                  currentUserId={currentUserIdReceived}
                  userUserId={user.id}
                />
              </div>

              <input
                type="radio"
                name="user-tabs"
                className="tab [&:checked]:bg-base-200 [&:checked]:font-bold"
                aria-label={t("followUps.title")}
              />
              <div className="tab-content border-base-300 bg-base-100 p-4 space-y-4 max-h-[450px] overflow-y-auto">
                <FollowUpsTab
                  userId={user.id}
                  currentUserId={currentUserIdReceived}
                />
              </div>
            </>
          )}
        </div>

        <div className="modal-action mt-6">
          <button
            className="btn rounded-full btn-outline"
            onClick={handleClose}
          >
            {t("adminUserTable.cancel")}
          </button>
          <button
            className="btn rounded-full bg-primary text-white hover:bg-accent"
            onClick={handleSave}
            disabled={isReadOnly}
          >
            {t("adminUserTable.save")}
          </button>
        </div>
      </div>
    </dialog>
  );
}
